import { Link } from "react-router";

import { WorkThumb } from "../lib/WorkThumb.tsx";
import { OndoCode } from "../lib/OndoCode.tsx";
import type { InventoryRow as Row } from "./gallery.model.ts";

/**
 * One work in the gallery's inventory.
 *
 * Built out of the same objects every other list in ONDO is — `WorkThumb`,
 * `Tag`, and a `Link` to the same work record the collector and the agent open.
 * There is no gallery-shaped copy of that record: what a gallery may do to a
 * work is decided in SQL, and a second screen would be a second opinion.
 */
export function InventoryRow({
  row,
  action,
  footer,
}: {
  row: Row;
  /** The row's own control — Sell when it is held, two answers when it is an offer. */
  action: React.ReactNode;
  /**
   * Anything that belongs UNDER the row rather than beside it.
   *
   * Added for the terms panel, which is the gallery's half of OND-101: a
   * proposal has a form, two answers and a history, and none of that fits in the
   * column a Sell button sits in. Optional, so an offer row renders exactly as
   * it did — the terms of an unanswered offer are not revisable.
   */
  footer?: React.ReactNode;
}) {
  return (
    <li className="gallery__row">
      <Link className="gallery__row-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />

        <div className="gallery__row-body">
          <h3 className="gallery__row-title">
            {row.title}
            {/* NOT `Tag`, and the reason is the one Tag's own docblock gives:
                "tone names the KIND of fact, not a colour", and a code state is
                not any of the seven kinds it has. It is not whether a step
                worked, not how good somebody's evidence is, and not what
                happened to the object — it is what the code's ladder says about
                the registry's record of this work. Its tokens are declared under
                their own heading in index.css for exactly that reason.

                THE WORDS AND THE MARK BELOW ARE ONE STATEMENT — OND-99. There is
                no condition here choosing between two labels any more, because
                there is no second answer to choose from: `row.code` and
                `row.codeFacts` came out of one `codeStateOf` call in the model, so
                this badge and the `<OndoCode>` below cannot disagree about the
                work they are both describing. */}
            <span className={`gallery__code gallery__code--${row.code}`}>
              {row.codeLabel}
            </span>
          </h3>
          <p className="gallery__row-identity">{row.identity}</p>
          <p className="gallery__row-terms">
            {row.terms}
            {row.price && (
              <>
                {" · "}
                <strong className="gallery__row-price">{row.price}</strong>
              </>
            )}
          </p>
          {/* Where the object is — OND-88 — only when the owner has opened it
              to this firm. Drawn as its own line because it is a different kind
              of fact from the terms above it: those are the deal, this is the
              crate. Nothing at all when closed; see `describeLocation`. */}
          {row.location && <p className="gallery__row-location">{row.location}</p>}
        </div>

        {/* The code itself, drawn. Renders nothing at all if the code cannot
            be encoded — the same failure path the sampled mark it replaced took
            for a photograph it could not read. */}
        <OndoCode code={row.ondoCode} facts={row.codeFacts} />
      </Link>

      {/* OUTSIDE the link, not inside it. A control nested in an anchor is a
          control that navigates when a click misses it by two pixels, and one of
          these controls commits a gallery to holding somebody's property. */}
      <div className="gallery__row-action">{action}</div>

      {/* Likewise outside the link, and for a sharper version of the same
          reason: this one holds a form. */}
      {footer && <div className="gallery__row-footer">{footer}</div>}
    </li>
  );
}
